import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { isAfter, isBefore, startOfDay, endOfDay } from 'date-fns';
import { AssignedStatus, ConversationStatus } from '@constants/conversations';
import { AssignmentType } from '@constants/inbox';
import { conversationsActions } from '@actions';

// Broadcast payloads are shared by every recipient, but unread state is per user —
// each client derives its own read flag from the views (unread user ids) list
export const deriveConversationRead = (conversation, userId) =>
    Array.isArray(conversation.views) ? !conversation.views.includes(userId) : conversation.read;

// Mirrors the backend ActiveInTimeframe predicate: open conversations created before
// the window's end; resolved ones must also have been closed after its start
const isActiveInTimeframe = (conversation, timeframe) => {
    if (!timeframe.start || !timeframe.end) {
        return true;
    }
    const from = startOfDay(new Date(timeframe.start));
    const to = endOfDay(new Date(timeframe.end));
    if (isAfter(new Date(conversation.createdAt), to)) {
        return false;
    }
    if (conversation.status === ConversationStatus.Open) {
        return true;
    }
    return !!conversation.closedAt && !isBefore(new Date(conversation.closedAt), from);
};

export const useConversations = () => {
    const { data } = useSelector(state => state.conversations);
    const currentUser = useSelector(state => state.auth.user);
    const { data: channels } = useSelector(state => state.channels);
    const conversationsRef = useRef([]);
    // The helpers below are captured once by the InboxPage socket subscription, so
    // they must read the user through a ref to survive auth loading after mount
    const currentUserRef = useRef(null);
    const channelsRef = useRef([]);

    const dispatch = useDispatch();

    useEffect(() => {
        conversationsRef.current = data;
        currentUserRef.current = currentUser;
        channelsRef.current = channels;
    }, [data, currentUser, channels]);

    const removeConversationById = id => {
        const filteredConversations = conversationsRef.current.filter(c => c.id !== id);
        dispatch(conversationsActions.update(filteredConversations));
    };

    const readConversationById = id => {
        const conversationsCopy = conversationsRef.current.slice();
        const updatedConversationIndex = conversationsCopy.findIndex(c => c.id === id);
        if (updatedConversationIndex !== -1) {
            conversationsCopy[updatedConversationIndex].read = true;
            dispatch(conversationsActions.update(conversationsCopy));
        }
    };

    // Mirrors the backend assignment predicate in GetConversationsQueryHandler so the
    // socket paths keep/insert exactly what a refetch would return
    const matchesAssignment = (conversation, filter) => {
        const { assignedUserId, assignedAgentId } = conversation;
        const isUnassigned = assignedUserId == null && assignedAgentId == null;

        switch (filter.assignment) {
            case AssignedStatus.Mine:
                return assignedUserId === currentUserRef.current?.id;
            case AssignedStatus.Unassigned:
                return isUnassigned;
            case AssignedStatus.Assigned: {
                const assignees = filter.assignees || [];
                const userSelected = assignees.some(a => a.type === AssignmentType.User && a.value === assignedUserId);
                const agentSelected = assignees.some(
                    a => a.type === AssignmentType.Agent && a.value === assignedAgentId,
                );
                const unassignedSelected = assignees.some(a => a.value === null);
                return userSelected || agentSelected || (unassignedSelected && isUnassigned);
            }
            default:
                return true;
        }
    };

    const isConversationMatchesFilter = (conversation, filter) => {
        const { teamId, channelId, status } = conversation;

        if (filter.collaboratingOnly && !(conversation.collaborators || []).includes(currentUserRef.current?.id)) {
            return false;
        }
        // INFO: check teams (empty selection means all accessible teams)
        if (filter.teams?.length > 0 && !filter.teams.includes(teamId)) {
            return false;
        }
        if (filter.channels?.length > 0) {
            if (!filter.channels.includes(channelId)) {
                return false;
            }
        } else {
            if (!channelId) {
                return false;
            }
            const conversationChannel = (channelsRef.current || []).find(channel => channel.id === channelId);
            if (conversationChannel && !conversationChannel.incoming) {
                return false;
            }
        }
        if (filter.tags?.length > 0 && !(conversation.tags || []).some(tag => filter.tags.includes(tag.id))) {
            return false;
        }
        // INFO: check status
        if (!filter.statuses.includes(status)) {
            return false;
        }
        // INFO: check assignment
        if (!matchesAssignment(conversation, filter)) {
            return false;
        }
        // INFO: check date range
        if (!isActiveInTimeframe(conversation, filter.timeframe)) {
            return false;
        }

        return true;
    };

    const updateConversationInFeedAndReturn = (conversation, filter, withUpdateCounters = false) => {
        const conversationIndex = conversationsRef.current.findIndex(c => c.id === conversation?.id);
        // Socket payloads can be partial — merge onto the cached row before filter-matching, or missing fields evict it
        const effectiveConversation =
            conversationIndex !== -1
                ? { ...conversationsRef.current[conversationIndex], ...conversation }
                : conversation;

        // INFO: skip or remove conversation if it doesn't match filter and update counters
        if (!isConversationMatchesFilter(effectiveConversation, filter)) {
            if (conversationIndex !== -1) {
                removeConversationById(conversation?.id);
            }
            dispatch(conversationsActions.getCounters(filter));
            return true;
        }

        if (conversationIndex !== -1) {
            // INFO: update conversation in feed if it exists
            const updatedConversations = conversationsRef.current.slice();
            updatedConversations[conversationIndex] = effectiveConversation;
            dispatch(conversationsActions.update(updatedConversations));
            // INFO: update counters if required (assign, solve, etc)
            if (withUpdateCounters) {
                dispatch(conversationsActions.getCounters(filter));
            }
            return true;
        }
        // INFO: return false to trigger return from function
        return false;
    };

    // Update-or-insert: a realtime event can make a conversation newly match this
    // client's scope (assigned to me, moved into my team) — it must appear without a refetch
    const upsertConversationInFeed = (conversation, filter) => {
        const isKnown = updateConversationInFeedAndReturn(conversation, filter, true);
        if (!isKnown) {
            dispatch(conversationsActions.update([...conversationsRef.current, conversation]));
            dispatch(conversationsActions.getCounters(filter));
        }
    };

    const updateConversationsChannelData = (channelId, channelStatus, isDeleted) => {
        const updatedConversations = conversationsRef.current.map(conversation => {
            return conversation.channelId === channelId
                ? { ...conversation, channelStatus, channelId: isDeleted ? null : channelId }
                : conversation;
        });
        dispatch(conversationsActions.update(updatedConversations));
    };

    return {
        readConversationById,
        removeConversationById,
        isConversationMatchesFilter,
        updateConversationInFeedAndReturn,
        upsertConversationInFeed,
        updateConversationsChannelData,
        conversationsRef,
    };
};
